import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useRouter } from 'next/router';

import MarketplaceMapLayout from '../../properties/shared/MarketplaceMapLayout';
import { ListingCard } from '../property/SimilarListings';
import type { ListingCardData } from '../property/types';
import { CityLinkList } from './CityLinkList';
import type { CityLink } from './CityLinkList';
import { DirectoryControls, DirectoryHeader } from './DirectoryHeader';
import { DirectorySeoHead } from './DirectorySeoHead';
import { StateLinkList } from './StateLinkList';
import type { StateLink } from './StateLinkList';

const PAGE_SIZE = 30;

interface DirectoryTemplateProps {
  canonicalUrl: string;
  citiesWithInventory: CityLink[];
  currentPage: number;
  listings: ListingCardData[];
  locationName: string;
  locationSlug: string;
  marketSlug?: string;
  nextPage?: number | null;
  seoDescription: string;
  seoTitle: string;
  statesWithInventory: StateLink[];
  totalCount: number;
  availableCities?: Array<{ name: string; slug: string }> | null;
}

export const DirectoryTemplate: React.FC<DirectoryTemplateProps> = (props) => {
  const {
    availableCities,
    canonicalUrl,
    citiesWithInventory,
    currentPage,
    listings,
    locationName,
    locationSlug,
    marketSlug,
    nextPage,
    seoDescription,
    seoTitle,
    statesWithInventory,
    totalCount,
  } = props;

  const router = useRouter();

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const showPagination = totalCount > PAGE_SIZE;
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = nextPage != null;

  const buildPageUrl = (page: number) => {
    const params = new URLSearchParams();
    Object.entries(router.query).forEach(([key, value]) => {
      if (key !== 'state' && key !== 'market' && typeof value === 'string') {
        params.set(key, value);
      }
    });
    params.set('page', String(page));
    const basePath = router.asPath.split('?')[0];
    return `${basePath}?${params.toString()}`;
  };

  return (
    <>
      <DirectorySeoHead
        canonicalUrl={canonicalUrl}
        listings={listings}
        locationName={locationName}
        seoDescription={seoDescription}
        seoTitle={seoTitle}
        totalCount={totalCount}
      />
      <MarketplaceMapLayout>
        <Col className="h-full w-full px-2 py-4">
          <Col className="h-full w-full max-w-300 self-center overflow-scroll pb-24 md:pb-0">
            <DirectoryHeader
              locationName={locationName}
              locationSlug={locationSlug}
              availableCities={availableCities}
              marketSlug={marketSlug || availableCities?.[0].slug}
            />

            <Box className="grid grid-cols-1 gap-6 pb-4 md:grid-cols-2">
              {listings.map((listing) => (
                <Box key={listing.addressToken}>
                  <ListingCard listing={listing} />
                </Box>
              ))}
            </Box>

            <DirectoryControls
              availableCities={availableCities}
              className="fixed inset-x-0 bottom-0 z-10 border-t border-od-border-tertiary bg-od-bg-primary px-4 py-3 md:hidden"
              locationSlug={locationSlug}
              marketSlug={marketSlug || availableCities?.[0].slug}
            />

            {showPagination && (
              <Row className="items-center justify-center gap-2 pb-4">
                <Button
                  variant="secondary"
                  aria-label="Go to previous page"
                  analyticsName="directory-previous-page"
                  className="size-7 rounded-full border-od-border-tertiary p-0"
                  {...(hasPreviousPage
                    ? { href: buildPageUrl(currentPage - 1) }
                    : { disabled: true })}
                >
                  <IconChevronLeft size={20} />
                </Button>
                <Text as="span" className="text-body-md">
                  Page {currentPage} of {totalPages}
                </Text>
                <Button
                  variant="secondary"
                  aria-label="Go to next page"
                  analyticsName="directory-next-page"
                  className="size-7 rounded-full border-od-border-tertiary p-0"
                  {...(hasNextPage ? { href: buildPageUrl(currentPage + 1) } : { disabled: true })}
                >
                  <IconChevronRight size={20} />
                </Button>
              </Row>
            )}

            <Row className="flex-col md:flex-row">
              <CityLinkList cities={citiesWithInventory} />
              <StateLinkList states={statesWithInventory} />
            </Row>
          </Col>
        </Col>
      </MarketplaceMapLayout>
    </>
  );
};
